/**
 * VirtualElementList — windowed object list for the editor sidebar.
 *
 * Only rendered when the CANVAS_EDITOR flag is ON. Renders a flat row model
 * (layer headers + element items) but keeps only the rows near the viewport in
 * the DOM, so a 3,000+ object map costs ~20 DOM nodes instead of ~3,000. The
 * click / hover / selected behavior of each row matches the non-virtualized
 * sidebar exactly.
 */

import { useEffect, useRef, useState, ReactNode } from 'react'
import { dia } from '@joint/core'
import styles from './EditorSidebar.module.css'

export type VirtualRow =
  | { kind: 'header'; key: string; layer: string; count: number; open: boolean }
  | { kind: 'item'; key: string; elementId: string; text: string; selected: boolean }

const ROW_HEIGHT = 42
const OVERSCAN = 6

interface VirtualElementListProps {
  rows: VirtualRow[]
  onToggleLayer: (layer: string) => void
  onObjectClick: (elementId: string) => void
  onObjectHover: (elementId: string | null) => void
}

export function VirtualElementList({
  rows,
  onToggleLayer,
  onObjectClick,
  onObjectHover,
}: VirtualElementListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewH, setViewH] = useState(0)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setViewH(el.clientHeight))
    ro.observe(el)
    setViewH(el.clientHeight)
    return () => ro.disconnect()
  }, [])

  const total = rows.length * ROW_HEIGHT
  const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN)
  const end = Math.min(rows.length, Math.ceil((scrollTop + viewH) / ROW_HEIGHT) + OVERSCAN)

  const visible: ReactNode[] = []
  for (let i = start; i < end; i++) {
    const row = rows[i]
    const top = i * ROW_HEIGHT
    if (row.kind === 'header') {
      visible.push(
        <div key={row.key} className={styles.virtualRow} style={{ top }}>
          <div
            className={styles.layerSummary}
            data-open={row.open ? 'true' : 'false'}
            onClick={() => onToggleLayer(row.layer)}
          >
            {row.layer} ({row.count})
          </div>
        </div>
      )
    } else {
      visible.push(
        <div key={row.key} className={styles.virtualRow} style={{ top }}>
          <div
            onClick={() => onObjectClick(row.elementId)}
            onMouseEnter={() => onObjectHover(row.elementId)}
            onMouseLeave={() => onObjectHover(null)}
            className={`${styles.elementItem} ${row.selected ? styles.elementItemSelected : ''}`}
          >
            {row.text}
          </div>
        </div>
      )
    }
  }

  return (
    <div
      ref={scrollRef}
      className={styles.virtualScroll}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ height: total, position: 'relative' }}>{visible}</div>
    </div>
  )
}

/** Build the flat row model from the layer map, honoring the layer filter and
 *  collapse state. Mirrors the non-virtualized list's filtering (drops grouped
 *  children via `parent`) and text resolution. */
export function buildVirtualRows(
  objectsByLayer: Map<string, dia.Element[]>,
  selectedLayer: string,
  selectedElementId: string | null,
  isLayerOpen: (layer: string) => boolean
): VirtualRow[] {
  const rows: VirtualRow[] = []
  const rowForElement = (element: dia.Element): VirtualRow => {
    const elementId = element.id as string
    const data = element.get('data')
    const text = data?.properties?.name || data?.groupName || elementId.slice(0, 8)
    return { kind: 'item', key: elementId, elementId, text, selected: selectedElementId === elementId }
  }

  if (selectedLayer === 'all') {
    for (const [layer, elements] of objectsByLayer.entries()) {
      const filtered = elements.filter((el) => !el.get('parent'))
      if (filtered.length === 0) continue
      const open = isLayerOpen(layer)
      rows.push({ kind: 'header', key: `__h_${layer}`, layer, count: filtered.length, open })
      if (open) for (const el of filtered) rows.push(rowForElement(el))
    }
  } else {
    const elements = objectsByLayer.get(selectedLayer)?.filter((el) => !el.get('parent')) || []
    for (const el of elements) rows.push(rowForElement(el))
  }
  return rows
}
